import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from '@/lib/useCookies';

// VAPID public key must match the backend's VAPID_PUBLIC_KEY
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export function usePushNotifications() {
    const [subscription, setSubscription] = useState<PushSubscription | null>(null);
    const [permission, setPermission] = useState<NotificationPermission>('default');

    // Helper to get API token
    const getAuthToken = useCallback(async () => {
        return await Cookies.getCookie('accessToken');
    }, []);

    const syncSubscription = useCallback(async (sub: PushSubscription) => {
        const token = await getAuthToken();
        if (!token) return;

        try {
            await axios.post(`${import.meta.env.VITE_API_BASE_URL}/notifications/subscribe`, {
                subscription: sub,
                userAgent: navigator.userAgent
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Cookies.setCookie('push_synced', 'true', 30);
            console.log('Push Subscription synced with backend');
        } catch (err) {
            console.error('Failed to sync existing subscription:', err);
        }
    }, [getAuthToken]);

    useEffect(() => {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            // Register SW
            navigator.serviceWorker.register('/sw.js')
                .then(registration => {
                    console.log('SW Registered:', registration);
                    return registration.pushManager.getSubscription();
                })
                .then(async (sub) => {
                    if (sub) {
                        setSubscription(sub);

                        // Check if we already synced this subscription
                        const isSynced = await Cookies.getCookie('push_synced');

                        if (!isSynced) {
                            await syncSubscription(sub);
                        } else {
                            console.log('Push Subscription already synced (cookie check)');
                        }
                    }
                })
                .catch(err => console.error('SW Registration failed:', err));

            setPermission(Notification.permission);
        }
    }, [syncSubscription]);

    const subscribe = useCallback(async () => {
        if (!('serviceWorker' in navigator)) return;

        try {
            const registration = await navigator.serviceWorker.ready;

            const sub = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
            });

            setSubscription(sub);
            await syncSubscription(sub);

            console.log('Push Subscribed!');
            setPermission(Notification.permission);
        } catch (error) {
            console.error('Failed to subscribe to push:', error);
        }
    }, [syncSubscription]);

    const unsubscribe = useCallback(async () => {
        if (!subscription) return;

        try {
            await subscription.unsubscribe();
            setSubscription(null);

            const token = await getAuthToken();
            if (token) {
                await axios.post(`${import.meta.env.VITE_API_BASE_URL}/notifications/unsubscribe`, {
                    endpoint: subscription.endpoint
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            // Remove sync cookie
            Cookies.deleteCookie('push_synced');

        } catch (err) {
            console.error('Unsubscribe failed:', err);
        }
    }, [subscription, getAuthToken]);

    return {
        subscription,
        permission,
        subscribe,
        unsubscribe
    };
}
